import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for leadership API path
 */
export const LEADERSHIP_API_PATH = new InjectionToken<string>('LEADERSHIP_API_PATH');

/**
 * Interface for leadership member data
 */
export interface LeadershipMember {
  id?: string;
  name: string;
  role: string;
  title?: string;
  rank: number;
  score?: number;
  bio?: string;
  email?: string;
  linkedin?: string;
  avatar?: string;
  category?: 'providers' | 'assets' | 'organisation';
  [key: string]: any;
}

/**
 * Interface for leadership board response
 */
export interface LeadershipBoardResponse {
  members?: LeadershipMember[];
  data?: LeadershipMember[];
  total?: number;
  page?: number;
  size?: number;
  [key: string]: any;
}

/**
 * Leaderboard API Service for handling Leaderboard-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class LeadershipApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(LEADERSHIP_API_PATH) private leadershipApiPath: string,
  ) {}

  /**
   * Get leadership board for providers
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leadership board data for providers
   */
  async getLeadershipForProvider(page: number = 1, size: number = 10): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}provider?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Provider API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('LeadershipApiService: Provider API response received:', response);
      return response;
    } catch (error) {
      console.error('Error fetching leadership for providers:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for assets
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for assets
   */
  async getLeaderboardForAssets(page: number = 1, size: number = 10): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}asset?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Assets API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('LeadershipApiService: Assets API response received:', response);
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for assets:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for organisations
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for organisations
   */
  async getLeaderboardForOrganisations(page: number = 1, size: number = 10): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}organization?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Organisation API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('LeadershipApiService: Organisation API response received:', response);
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for organisations:', error);
      throw error;
    }
  }
}
